import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { ApiConnectService } from './api-connect.service';
import {
  CeoClothingLossDecisionPayload,
  CeoPendingLossCase,
  ClothingCatalogItem,
  ClothingCatalogItemList,
  ClothingCatalogItemPayload,
  ClothingIssueStats,
  ClothingIssueStatsFilters,
  ClothingUserOption,
  HeadClothingManualExtensionPayload,
  HeadClothingLossDecisionPayload,
  HeadPendingLossCase,
  ClothingLossCase,
  ClothingLossCasePayload,
  ClothingMarker,
  ClothingConfiguredMarker,
  ClothingConfiguredMarkerPayload,
  ClothingMarkerNorm,
  ClothingMarkerNormPayload,
  ClothingRequest,
  ClothingRequestPayload,
  ClothingSettings,
  ClothingPersonalNorm,
  ClothingPersonalNormPayload,
  ClothingStorekeeperAssignment,
  ClothingStorekeeperAssignmentPayload,
  EmployeeClothingEntitlement,
  EmployeeClothingEntitlementsSource,
  EmployeeClothingOverview,
  EmployeeClothingPosition,
  StorekeeperEmployee,
  StorekeeperIssuePayload,
  StorekeeperRejectNoStockPayload
} from '../models/clothing.model';

@Injectable()
export class ClothingService {
  constructor(
    private http: HttpClient,
    private apiConnectService: ApiConnectService
  ) {}

  getCatalogItems(activeOnly: boolean): Observable<ClothingCatalogItemList[]> {
    var params = new HttpParams().set('activeOnly', String(activeOnly));

    return this.http.get<ClothingCatalogItemList[]>(
      this.apiConnectService.clothingCatalog,
      { params: params }
    );
  }

  getCatalogItem(itemId: string): Observable<ClothingCatalogItem> {
    return this.http.get<ClothingCatalogItem>(
      this.apiConnectService.clothingCatalog + '/' + itemId
    );
  }

  getMarkers(): Observable<ClothingMarker[]> {
    return this.http.get<ClothingMarker[]>(
      this.apiConnectService.clothingMarkers
    );
  }

  getConfiguredMarkers(): Observable<ClothingConfiguredMarker[]> {
    return this.http.get<ClothingConfiguredMarker[]>(
      this.apiConnectService.clothingConfiguredMarkers
    );
  }

  createConfiguredMarker(
    payload: ClothingConfiguredMarkerPayload
  ): Observable<ClothingConfiguredMarker> {
    return this.http.post<ClothingConfiguredMarker>(
      this.apiConnectService.clothingMarkers,
      payload
    );
  }

  updateConfiguredMarker(
    markerId: string,
    payload: ClothingConfiguredMarkerPayload
  ): Observable<ClothingConfiguredMarker> {
    return this.http.put<ClothingConfiguredMarker>(
      this.apiConnectService.clothingMarkers + '/' + markerId,
      payload
    );
  }

  deleteConfiguredMarker(markerId: string): Observable<Object> {
    return this.http.delete(this.apiConnectService.clothingMarkers + '/' + markerId);
  }

  getSettings(): Observable<ClothingSettings> {
    return this.http.get<ClothingSettings>(
      this.apiConnectService.clothingSettings
    );
  }

  updateSettings(payload: ClothingSettings): Observable<ClothingSettings> {
    return this.http.put<ClothingSettings>(
      this.apiConnectService.clothingSettings,
      payload
    );
  }

  getEmployeeOverview(): Observable<EmployeeClothingOverview> {
    return this.http.get<EmployeeClothingOverview>(
      this.apiConnectService.employeeClothingOverview
    );
  }

  getEmployeeEntitlements(): Observable<EmployeeClothingEntitlementsSource> {
    return this.http.get<EmployeeClothingEntitlementsSource>(
      this.apiConnectService.employeeClothingEntitlements
    );
  }

  getHeadEmployeePositions(userId: string): Observable<EmployeeClothingPosition[]> {
    return this.http.get<EmployeeClothingPosition[]>(
      this.apiConnectService.headClothingPositions + userId + '/positions'
    );
  }

  getHeadPendingLossCases(): Observable<HeadPendingLossCase[]> {
    return this.http.get<HeadPendingLossCase[]>(
      this.apiConnectService.headClothingPendingLossCases
    );
  }

  getCeoPendingLossCases(): Observable<CeoPendingLossCase[]> {
    return this.http.get<CeoPendingLossCase[]>(
      this.apiConnectService.ceoClothingPendingLossCases
    );
  }

  decideHeadLossCase(
    lossCaseId: string,
    payload: HeadClothingLossDecisionPayload
  ): Observable<ClothingLossCase> {
    return this.http.patch<ClothingLossCase>(
      this.apiConnectService.headClothingLossCaseActions + lossCaseId + '/decision',
      payload
    );
  }

  decideCeoLossCase(
    lossCaseId: string,
    payload: CeoClothingLossDecisionPayload
  ): Observable<ClothingLossCase> {
    return this.http.patch<ClothingLossCase>(
      this.apiConnectService.ceoClothingLossCaseActions + lossCaseId + '/decision',
      payload
    );
  }

  extendHeadPosition(
    positionId: string,
    payload: HeadClothingManualExtensionPayload
  ): Observable<EmployeeClothingPosition> {
    return this.http.post<EmployeeClothingPosition>(
      this.apiConnectService.headClothingPositionActions + positionId + '/manual-extension',
      payload
    );
  }

  createEmployeeRequest(payload: ClothingRequestPayload): Observable<ClothingRequest> {
    return this.http.post<ClothingRequest>(
      this.apiConnectService.employeeClothingRequests,
      payload
    );
  }

  createEmployeeLossCase(payload: ClothingLossCasePayload): Observable<ClothingLossCase> {
    return this.http.post<ClothingLossCase>(
      this.apiConnectService.employeeClothingLossCases,
      payload
    );
  }

  getStorekeeperEmployeeOverview(userId: string): Observable<EmployeeClothingOverview> {
    return this.http.get<EmployeeClothingOverview>(
      this.apiConnectService.storekeeperClothingOverview + userId + '/overview'
    );
  }

  getStorekeeperEmployeeEntitlements(userId: string): Observable<EmployeeClothingEntitlementsSource> {
    return this.http.get<EmployeeClothingEntitlementsSource>(
      this.apiConnectService.storekeeperClothingEntitlements + userId + '/entitlements'
    );
  }

  getAdminEmployeeOverview(userId: string): Observable<EmployeeClothingOverview> {
    return this.http.get<EmployeeClothingOverview>(
      this.apiConnectService.adminClothingOverview + userId + '/overview'
    );
  }

  getAdminEmployeeEntitlements(userId: string): Observable<EmployeeClothingEntitlementsSource> {
    return this.http.get<EmployeeClothingEntitlementsSource>(
      this.apiConnectService.adminClothingEntitlements + userId + '/entitlements'
    );
  }

  getStorekeeperEmployees(query: string): Observable<StorekeeperEmployee[]> {
    var params = new HttpParams();
    var value = this.normalizeQuery(query);

    if (value.length) {
      params = params.set('q', value);
    }

    return this.http.get<StorekeeperEmployee[]>(
      this.apiConnectService.storekeeperClothingEmployees,
      { params: params }
    );
  }

  directIssueStorekeeperItem(
    userId: string,
    catalogItemId: string,
    payload: StorekeeperIssuePayload
  ): Observable<EmployeeClothingPosition> {
    return this.http.post<EmployeeClothingPosition>(
      this.createStorekeeperDirectIssueUrl(userId, catalogItemId),
      payload
    );
  }

  directIssueAdminItem(
    userId: string,
    catalogItemId: string,
    payload: StorekeeperIssuePayload
  ): Observable<EmployeeClothingPosition> {
    return this.http.post<EmployeeClothingPosition>(
      this.createAdminDirectIssueUrl(userId, catalogItemId),
      payload
    );
  }

  issueStorekeeperRequest(
    requestId: string,
    payload: StorekeeperIssuePayload
  ): Observable<ClothingRequest> {
    return this.http.patch<ClothingRequest>(
      this.apiConnectService.storekeeperClothingRequests + '/' + requestId + '/issue',
      payload
    );
  }

  rejectStorekeeperRequest(
    requestId: string,
    payload: StorekeeperRejectNoStockPayload
  ): Observable<ClothingRequest> {
    return this.http.patch<ClothingRequest>(
      this.apiConnectService.storekeeperClothingRequests + '/' + requestId + '/reject-no-stock',
      payload
    );
  }

  getStorekeeperRequest(requestId: string): Observable<ClothingRequest> {
    return this.http.get<ClothingRequest>(
      this.apiConnectService.storekeeperClothingRequests + '/' + requestId
    );
  }

  createCatalogItem(
    payload: ClothingCatalogItemPayload
  ): Observable<ClothingCatalogItem> {
    return this.http.post<ClothingCatalogItem>(
      this.apiConnectService.clothingCatalog,
      payload
    );
  }

  updateCatalogItem(
    itemId: string,
    payload: ClothingCatalogItemPayload
  ): Observable<ClothingCatalogItem> {
    return this.http.put<ClothingCatalogItem>(
      this.apiConnectService.clothingCatalog + '/' + itemId,
      payload
    );
  }

  deactivateCatalogItem(itemId: string): Observable<Object> {
    return this.http.delete(this.apiConnectService.clothingCatalog + '/' + itemId);
  }

  getNormsByMarker(markerCode: string): Observable<ClothingMarkerNorm[]> {
    return this.http.get<ClothingMarkerNorm[]>(
      this.apiConnectService.clothingNormsByMarker + '/' + markerCode
    );
  }

  createNorm(payload: ClothingMarkerNormPayload): Observable<ClothingMarkerNorm> {
    return this.http.post<ClothingMarkerNorm>(
      this.apiConnectService.clothingNorms,
      payload
    );
  }

  updateNorm(
    normId: string,
    payload: ClothingMarkerNormPayload
  ): Observable<ClothingMarkerNorm> {
    return this.http.put<ClothingMarkerNorm>(
      this.apiConnectService.clothingNorms + '/' + normId,
      payload
    );
  }

  deactivateNorm(normId: string): Observable<Object> {
    return this.http.delete(this.apiConnectService.clothingNorms + '/' + normId);
  }

  getPersonalNormsByUser(userId: string): Observable<ClothingPersonalNorm[]> {
    return this.http.get<ClothingPersonalNorm[]>(
      this.apiConnectService.clothingPersonalNormsByUser + '/' + userId
    );
  }

  createPersonalNorm(
    payload: ClothingPersonalNormPayload
  ): Observable<ClothingPersonalNorm> {
    return this.http.post<ClothingPersonalNorm>(
      this.apiConnectService.clothingPersonalNorms,
      payload
    );
  }

  updatePersonalNorm(
    normId: string,
    payload: ClothingPersonalNormPayload
  ): Observable<ClothingPersonalNorm> {
    return this.http.put<ClothingPersonalNorm>(
      this.apiConnectService.clothingPersonalNorms + '/' + normId,
      payload
    );
  }

  deletePersonalNorm(normId: string): Observable<Object> {
    return this.http.delete(this.apiConnectService.clothingPersonalNorms + '/' + normId);
  }

  getStorekeeperAssignments(): Observable<ClothingStorekeeperAssignment[]> {
    return this.http.get<ClothingStorekeeperAssignment[]>(
      this.apiConnectService.clothingStorekeeperAssignments
    );
  }

  getStorekeeperOptions(): Observable<ClothingUserOption[]> {
    return this.http.get<ClothingUserOption[]>(
      this.apiConnectService.clothingStorekeeperOptions
    );
  }

  createStorekeeperAssignment(
    payload: ClothingStorekeeperAssignmentPayload
  ): Observable<ClothingStorekeeperAssignment> {
    return this.http.post<ClothingStorekeeperAssignment>(
      this.apiConnectService.clothingStorekeeperAssignments,
      payload
    );
  }

  updateStorekeeperAssignment(
    assignmentId: string,
    payload: ClothingStorekeeperAssignmentPayload
  ): Observable<ClothingStorekeeperAssignment> {
    return this.http.put<ClothingStorekeeperAssignment>(
      this.apiConnectService.clothingStorekeeperAssignments + '/' + assignmentId,
      payload
    );
  }

  deactivateStorekeeperAssignment(assignmentId: string): Observable<Object> {
    return this.http.delete(
      this.apiConnectService.clothingStorekeeperAssignments + '/' + assignmentId
    );
  }

  getIssuedStats(filters: ClothingIssueStatsFilters): Observable<ClothingIssueStats[]> {
    var params = this.createIssuedStatsParams(filters);

    return this.http.get<ClothingIssueStats[]>(
      this.apiConnectService.clothingIssuedStats,
      { params: params }
    );
  }

  private createIssuedStatsParams(filters: ClothingIssueStatsFilters): HttpParams {
    var params = new HttpParams().set('from', filters.from).set('to', filters.to);

    params = this.appendIssuedStatsParam(params, 'catalogItemId', filters.catalogItemId);
    return this.appendIssuedStatsParam(params, 'storekeeperUserId', filters.storekeeperUserId);
  }

  private appendIssuedStatsParam(
    params: HttpParams,
    key: string,
    value: string
  ): HttpParams {
    if (!value || !value.length) {
      return params;
    }
    return params.set(key, value);
  }

  private createStorekeeperDirectIssueUrl(
    userId: string,
    catalogItemId: string
  ): string {
    return this.apiConnectService.storekeeperClothingDirectIssue
      + userId
      + '/items/'
      + catalogItemId
      + '/issue';
  }

  private createAdminDirectIssueUrl(
    userId: string,
    catalogItemId: string
  ): string {
    return this.apiConnectService.adminClothingDirectIssue
      + userId
      + '/catalog/'
      + catalogItemId
      + '/issue';
  }

  private normalizeQuery(query: string): string {
    if (!query) {
      return '';
    }
    return query.trim();
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/services/clothing.service.ts